import { EEvent } from '@customTypes/events';
import { TEventBus } from '@eventBus/EventBus';
import { Fragment } from '@fragment/Fragment';
import { ResolutionsManagerEvents } from '@resolutionsManager/ResolutionsManager.events';
import { BasePlaylistRotationControllerEvents } from '@streamLoader/BasePlaylistRotationController/BasePlaylistRotationController.events';
import { FragmentLoaderEvents } from '@streamLoader/FragmentLoader/FragmentLoader.events';
import {NetworkLoaderEvent, TNetworkLoaderEvents} from '@streamLoader/net/NetworkLoader.events';
import { INetworkLoadStats } from '@streamLoader/net/helpers/NetworkLoadStats';
import bind from '@utils/decorators/bind';
import { IHydratedManifest } from '@utils/m3u8Hydrator/m3u8Hydrator';

import { DebuggerEvents } from '../Debugger/Debugger.events';

export class LegacyEvents {
  public _eventBus: TEventBus;
  private _isResolutionSelected: boolean = false;

  private readonly _sendEvent: (ev: CustomEvent) => void;
  private readonly _onPlaylistLoadingStartedListener: string;
  private readonly _onPlaylistLoadedListener: string;
  private readonly _onPlaylistParsedListener: string;
  private readonly _onFragmentLoadedListener: string;
  private readonly _onDebuggerConnectedListener: string;
  private readonly _onResolutionChangedListener: string;
  private readonly _onResolutionChangedAutoListener: string;
  private readonly _onNetworkRequestSettledListener: string;
  private _isDebugEnabled: boolean;

  constructor(sendEvent: (ev: CustomEvent) => void, eventBus: TEventBus) {
    this._eventBus = eventBus;
    this._sendEvent = sendEvent;

    this._isDebugEnabled = document?.cookie?.includes('__mmpDebug__=1');

    this._onPlaylistLoadingStartedListener = this._eventBus.subscribe(
      BasePlaylistRotationControllerEvents.playlists.playlistRequested,
      this._onPlaylistLoadingStarted,
    );

    this._onResolutionChangedListener = this._eventBus.subscribe(ResolutionsManagerEvents.resolutionChanged, this._onResolutionChanged);
    this._onResolutionChangedAutoListener = this._eventBus.subscribe(
      ResolutionsManagerEvents.resolutionAutoChanged,
      this._onResolutionAutoChanged,
    );

    this._onPlaylistLoadedListener = this._eventBus.subscribe(
      BasePlaylistRotationControllerEvents.playlists.playlistLoaded,
      this._onPlaylistLoaded,
    );

    this._onPlaylistParsedListener = this._eventBus.subscribe(
      BasePlaylistRotationControllerEvents.playlists.playlistInfo,
      this._onPlaylistInfoAvailable,
    );

    this._onFragmentLoadedListener = this._eventBus.subscribe(FragmentLoaderEvents.fragments.fragmentLoaded, this._onFragmentLoaded);

    this._onDebuggerConnectedListener = this._eventBus.subscribe(DebuggerEvents.debuggerConnected, this._onDebuggerConnected);
    this._onNetworkRequestSettledListener = this._eventBus.subscribe(
      NetworkLoaderEvent.events.networkRequestSettled,
      this._onNetworkRequestSettled,
    );
  }

  public destroy(): void {
    this._eventBus.unsubscribe(this._onPlaylistLoadingStartedListener);
    this._eventBus.unsubscribe(this._onPlaylistLoadedListener);
    this._eventBus.unsubscribe(this._onPlaylistParsedListener);
    this._eventBus.unsubscribe(this._onFragmentLoadedListener);
    this._eventBus.unsubscribe(this._onDebuggerConnectedListener);
    this._eventBus.unsubscribe(this._onResolutionChangedListener);
    this._eventBus.unsubscribe(this._onResolutionChangedAutoListener);
    this._eventBus.unsubscribe(this._onNetworkRequestSettledListener);
  }

  @bind
  private _onPlaylistLoadingStarted(): void {
    if (!this._isResolutionSelected) {
      this._sendEvent(new CustomEvent(EEvent.MANIFEST_LOADING));
    }
  }

  @bind
  private _onResolutionChanged(): void {
    this._isResolutionSelected = true;
  }

  @bind
  private _onPlaylistLoaded(data: { originalUrl: string; realUrl: string; stats: INetworkLoadStats }): void {
    const { originalUrl, realUrl, stats } = data;
    if (this._isResolutionSelected) {
      this._sendEvent(new CustomEvent(EEvent.LEVEL_LOADED, { detail: { netStats: stats } }));
      return;
    }

    this._sendEvent(new CustomEvent(EEvent.MANIFEST_LOADED, { detail: { originalUrl, realUrl } }));
  }

  @bind
  private _onPlaylistInfoAvailable(data: IHydratedManifest): void {
    this._sendEvent(new CustomEvent(EEvent.MANIFEST_PARSED, { detail: data }));
  }

  @bind
  private _onFragmentLoaded(fragment: Fragment): void {
    this._sendEvent(new CustomEvent(EEvent.FRAG_LOADED, { detail: { netStats: fragment.stats, duration: fragment.duration } }));
  }

  @bind
  private _onDebuggerConnected(url: string): void {
    this._sendEvent(new CustomEvent(EEvent.DEBUGGER_CONNECTED, { detail: { panelUrl: url } }));
  }

  @bind
  private _onResolutionAutoChanged({ prevResolution, nextResolution }: { prevResolution?: string; nextResolution: string }): void {
    this._sendEvent(new CustomEvent(EEvent.LEVEL_SWITCHED_AUTO, { detail: { prevResolution, nextResolution } }));
  }

  @bind
  private _onNetworkRequestSettled(requestInfo: TNetworkLoaderEvents[typeof NetworkLoaderEvent.events.networkRequestSettled]): void {
    if (this._isDebugEnabled) {
      globalThis.dispatchEvent(new CustomEvent('mmpFetchSettled', { detail: { requestInfo } }));
    }
  }
}
